import React, { useState } from 'react'

import { Tooltip, Theme } from '@material-ui/core'
import {
    BaseWorkorder,
    Schedule,
    ezScheduleDetails,
    TemplateService,
} from '../../../models'
import { LineItem } from '../../../models/WorkOrder/types'
import { calculateTotalInvoiceForecast, formatTotals } from '../services'
import { getChangeOrderPrice } from '../../../models/WorkOrder/services'
import {
    LineItemTotals,
    Totals,
} from '../../../containers/Scheduler_v2/Modals/BudgetModal'
import { useSchedule } from '../../../hooks'

import { UpdateScheduleRequest } from '../../../store'
import InfoIcon from '@material-ui/icons/Info'
import { Container } from '../../../components'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import { HeaderValue, MoneyInputValue, MoneyValue } from '../styles'
import NumberFormat from 'react-number-format'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    workorders: BaseWorkorder[]
    totals: Totals | undefined
    lineItemTotals: LineItemTotals | undefined
    scheduleDetail: Schedule | undefined
    portfolio: boolean
    /** Total actual pay from payroll tracker (optional, shown when provided). */
    payrollTotal?: number
    theme: Theme
    ezSchedule: ezScheduleDetails
}

export const SummaryTable = (props: Props) => {
    const {
        theme,
        workorders,
        totals,
        scheduleDetail,
        lineItemTotals,
        portfolio,
        payrollTotal,
        ezSchedule,
    } = props

    const totalInvoiceForecast = calculateTotalInvoiceForecast(workorders)

    const { updateSchedule } = useSchedule()
    const [baselineBudget, setBaselineBudget] = useState(
        scheduleDetail?.baseline_budget || 0,
    )

    const req: UpdateScheduleRequest = {
        scheduleId: scheduleDetail?.id ?? -1,
        body: {
            baseline_budget: Number(baselineBudget),
        },
    }
    // use first phase in array as the selected phase
    const templateServices = ezSchedule.phases[0]?.template_services ?? []

    const totalServiceBudget = React.useMemo(
        () =>
            templateServices.reduce(
                (acc: number, ts: TemplateService) => acc + Number(ts.budget),
                0,
            ),
        [templateServices],
    )
    const totalLineItems = React.useMemo(
        () =>
            ezSchedule.line_items.reduce(
                (acc: number, li: LineItem) => acc + Number(li.cost),
                0,
            ),
        [ezSchedule.line_items],
    )
    const totalApprovedChangeOrders = React.useMemo(
        () => workorders.reduce((acc, wo) => acc + getChangeOrderPrice(wo), 0),
        [workorders],
    )

    const summaryLabelStyle = {
        justifyContent: 'flex-start' as const,
        whiteSpace: 'nowrap' as const,
    }

    return (
        <Box sx={{ flexGrow: 1, padding: 2 }}>
            <Container
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    flex: 1,
                    minHeight: 40,
                }}
            >
                {/* Title */}
                <span
                    style={{
                        ...typographyStyle(theme.typography.h6),
                        fontWeight: 700,
                        justifyContent: 'center',
                        alignItems: 'center',
                        flex: 1,
                        display: 'flex',
                    }}
                >
                    Summary
                </span>
            </Container>
            <Grid container spacing={0}>
                {/* Header Row */}
                <Grid item xs={4}>
                    <HeaderValue style={summaryLabelStyle}>
                        <Tooltip title="Total set budget for the project.">
                            <InfoIcon
                                fontSize="small"
                                color="inherit"
                                style={{
                                    marginRight: theme.spacing(1),
                                }}
                            ></InfoIcon>
                        </Tooltip>
                        Schedule
                    </HeaderValue>
                </Grid>
                <Grid item xs={2}>
                    <MoneyInputValue>
                        <NumberFormat
                            value={baselineBudget}
                            getInputRef={(input: HTMLInputElement | null) => {
                                input &&
                                    input.addEventListener(
                                        'focus',
                                        (event: FocusEvent) => {
                                            const target = event.target as HTMLInputElement
                                            target.select()
                                        },
                                    )
                            }}
                            onBlur={() => {
                                updateSchedule(req, {
                                    portfolio: portfolio,
                                })
                            }}
                            onChange={(e: any) => {
                                const value = Number(
                                    e.target.value.replace(/[^0-9.-]+/g, ''),
                                )
                                if (isNaN(value)) {
                                    return
                                }
                                setBaselineBudget(value)
                            }}
                            thousandSeparator={true}
                            decimalScale={0}
                            prefix={`$`}
                            style={{
                                // ...typographyStyle(theme.typography.subtitle2),
                                fontWeight:
                                    props.theme.typography.fontWeightBold,
                                textAlign: 'right',
                                border: `1px solid ${props.theme.palette.grey[400]}`,
                                borderRadius: props.theme.shape.borderRadius,
                                padding: props.theme.spacing(0.25, 1),
                                width: 80,
                            }}
                        />
                    </MoneyInputValue>
                </Grid>
                <Grid item xs={4}>
                    <HeaderValue style={summaryLabelStyle}>
                        <Tooltip title="Forecast + Payroll + Line Items">
                            <InfoIcon
                                fontSize="small"
                                color="inherit"
                                style={{
                                    marginRight: theme.spacing(1),
                                }}
                            />
                        </Tooltip>
                        FPL
                    </HeaderValue>
                </Grid>
                <Grid item xs={2}>
                    <MoneyValue>
                        {formatTotals(
                            totalInvoiceForecast.totalForecast +
                                (payrollTotal ?? 0) +
                                (totalLineItems ?? 0),
                        )}
                    </MoneyValue>
                </Grid>
                <Grid item xs={4}>
                    <HeaderValue style={summaryLabelStyle}>
                        <Tooltip title="Total forecasted cost of the project.">
                            <InfoIcon
                                fontSize="small"
                                color="inherit"
                                style={{
                                    marginRight: theme.spacing(1),
                                }}
                            ></InfoIcon>
                        </Tooltip>
                        Forecast
                    </HeaderValue>
                </Grid>
                <Grid item xs={2}>
                    <MoneyValue>
                        {formatTotals(totalInvoiceForecast.totalForecast)}
                    </MoneyValue>
                </Grid>
                <Grid item xs={4}>
                    <HeaderValue style={summaryLabelStyle}>
                        <Tooltip title="Total set budget for services.">
                            <InfoIcon
                                fontSize="small"
                                color="inherit"
                                style={{
                                    marginRight: theme.spacing(1),
                                }}
                            ></InfoIcon>
                        </Tooltip>
                        Services
                    </HeaderValue>
                </Grid>
                <Grid item xs={2}>
                    <MoneyValue>{formatTotals(totalServiceBudget)}</MoneyValue>
                </Grid>
                <Grid item xs={4}>
                    <HeaderValue style={summaryLabelStyle}>
                        <Tooltip title="Total dollars invoiced in schedule.">
                            <InfoIcon
                                fontSize="small"
                                color="inherit"
                                style={{
                                    marginRight: theme.spacing(1),
                                }}
                            ></InfoIcon>
                        </Tooltip>
                        Invoiced
                    </HeaderValue>
                </Grid>
                <Grid item xs={2}>
                    <MoneyValue>
                        {formatTotals(totalInvoiceForecast.totalInvoice)}
                    </MoneyValue>
                </Grid>
                <Grid item xs={4}>
                    <HeaderValue style={summaryLabelStyle}>
                        <Tooltip title="Total budget for line items">
                            <InfoIcon
                                fontSize="small"
                                color="inherit"
                                style={{
                                    marginRight: theme.spacing(1),
                                }}
                            ></InfoIcon>
                        </Tooltip>
                        Line Items
                    </HeaderValue>
                </Grid>
                <Grid item xs={2}>
                    <MoneyValue>{formatTotals(totalLineItems)}</MoneyValue>
                </Grid>
                <Grid item xs={4}>
                    <HeaderValue style={summaryLabelStyle}>
                        <Tooltip title="Approved Change Orders">
                            <InfoIcon
                                fontSize="small"
                                color="inherit"
                                style={{
                                    marginRight: theme.spacing(1),
                                }}
                            />
                        </Tooltip>
                        Approved COs
                    </HeaderValue>
                </Grid>
                <Grid item xs={2}>
                    <MoneyValue>
                        {formatTotals(totalApprovedChangeOrders)}
                    </MoneyValue>
                </Grid>
                {payrollTotal !== undefined && (
                    <>
                        <Grid item xs={4}>
                            <HeaderValue style={summaryLabelStyle}>
                                <Tooltip title="Total actual pay from Payroll Tracker (Total Summary row).">
                                    <InfoIcon
                                        fontSize="small"
                                        color="inherit"
                                        style={{
                                            marginRight: theme.spacing(1),
                                        }}
                                    />
                                </Tooltip>
                                Payroll
                            </HeaderValue>
                        </Grid>
                        <Grid item xs={2}>
                            <MoneyValue>
                                {formatTotals(payrollTotal)}
                            </MoneyValue>
                        </Grid>
                    </>
                )}
            </Grid>
        </Box>
    )
}
